import { ChatChartSpecDto, ChatTableSpecDto } from 'src/api/FiretrolApiClient';
import { chartValueAt, formatChartValue } from 'src/app/chat/chatChartFormat';
import {
  TABLE_EMPTY_TEXT,
  tableDataGrid,
  tableTruncationText,
} from 'src/app/chat/chatTableFormat';
import { entityRefUrl } from 'src/app/chat/entityRefs';
import { ITranscriptFormatOptions } from 'src/app/chat/transcript';
import { IChatToolActivity } from 'src/hooks/useChat';

/** Row-count summary for one tool call, e.g. "1,204 rows (truncated)"; null when no count was reported. */
export const formatToolRowCount = (
  activity: IChatToolActivity,
  options: ITranscriptFormatOptions = {},
): string | null => {
  if (activity.rowCount === undefined) return null;
  const count = new Intl.NumberFormat(options.locale).format(activity.rowCount);
  const noun = activity.rowCount === 1 ? 'row' : 'rows';
  return `${count} ${noun}${activity.truncated ? ' (truncated)' : ''}`;
};

export interface IChartDataTable {
  header: string[];
  rows: string[][];
}

/** A chart's data as display strings: the x label then one column per series, values formatted by unit. Null when the chart has no series or no points. */
export const chartDataTable = (
  chart: ChatChartSpecDto,
): IChartDataTable | null => {
  const series = chart.series ?? [];
  const points = chart.points ?? [];
  if (series.length === 0 || points.length === 0) return null;
  return {
    header: [chart.xLabel, ...series.map((s) => s.label)],
    rows: points.map((point) => [
      point.x,
      ...series.map((s, index) =>
        formatChartValue(chartValueAt(point, index), s.unit),
      ),
    ]),
  };
};

export const CHART_UNAVAILABLE_TEXT = 'This chart cannot be displayed.';

const markdownCell = (value: string): string =>
  value.replace(/\r?\n/g, ' ').replace(/[\\|[\]]/g, '\\$&');

const markdownRow = (cells: string[]): string => `| ${cells.join(' | ')} |`;

export const markdownInline = (value: string): string =>
  value
    .replace(/\s*[\r\n]+\s*/g, ' ')
    .trim()
    .replace(/[\\`*_[\]<>~#|]/g, '\\$&');

export const markdownBoldTitle = (title: string): string =>
  `**${markdownInline(title)}**`;

export const chartToMarkdown = (chart: ChatChartSpecDto): string => {
  const title = markdownBoldTitle(chart.title);
  const table = chartDataTable(chart);
  if (!table) return `${title}\n\n_${CHART_UNAVAILABLE_TEXT}_`;
  const line = (cells: string[]) => markdownRow(cells.map(markdownCell));
  return [
    title,
    '',
    line(table.header),
    `| --- |${' ---: |'.repeat(table.header.length - 1)}`,
    ...table.rows.map(line),
  ].join('\n');
};

export const tableToMarkdown = (
  table: ChatTableSpecDto,
  appOrigin: string | undefined,
): string => {
  const grid = tableDataGrid(table);
  const columns = table.columns ?? [];
  const cell = (text: string, rowIndex: number, columnIndex: number) => {
    const escaped = markdownCell(text);
    const url = entityRefUrl(columns[columnIndex].refs?.[rowIndex], appOrigin);
    return url === null ? escaped : `[${escaped}](${url})`;
  };
  const parts = table.title ? [markdownBoldTitle(table.title)] : [];
  if (grid.rows.length === 0 || grid.header.length === 0) {
    if (!table.truncated) parts.push(`_${TABLE_EMPTY_TEXT}_`);
  } else {
    parts.push(
      [
        markdownRow(grid.header.map(markdownCell)),
        `|${grid.numeric
          .map((numeric) => (numeric ? ' ---: |' : ' --- |'))
          .join('')}`,
        ...grid.rows.map((row, rowIndex) =>
          markdownRow(
            row.map((text, columnIndex) => cell(text, rowIndex, columnIndex)),
          ),
        ),
      ].join('\n'),
    );
  }
  if (table.truncated) parts.push(`_${tableTruncationText(grid.rows.length)}_`);
  return parts.join('\n\n');
};
